import { useEffect, useRef, useState } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { dimensions } from '../../data/homeDimensions'
import FigmaIconFrame from '../common/FigmaIconFrame'
import { mobilePaddingX, MOBILE_MQ } from '../../theme/mobileLayout'
import { leadFontSize, scale, sectionTitleFontSize } from '../../theme/scale'
import { useDimensionsScroll } from '../../hooks/useDimensionsScroll'
import { useReducedMotion } from '../../hooks/useReducedMotion'

const SECTION_SUBTITLE =
  'Driving holistic youth development through seven pillars of growth, service, and nation-building.'

/** Scroll reveal order and column — matches Figma Component 134 */
const CARD_LAYOUT = [
  { slug: 'education', side: 'left' },
  { slug: 'health', side: 'right' },
  { slug: 'environment', side: 'left' },
  { slug: 'democracy', side: 'right' },
  { slug: 'spirituality', side: 'left' },
  { slug: 'fitness', side: 'right' },
  { slug: 'student-welfare', side: 'left' },
]

const DESKTOP_CARD_WIDTH = 440
const CARD_SIDE_INSET = { lg: 32, xl: 48 }

function getDimension(slug) {
  return dimensions.find((dim) => dim.path === `/dimensions/${slug}`)
}

function useInViewReveal(ref) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return undefined

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [ref])

  return visible
}

function revealTransformMobile(progress) {
  const hiddenY = 120 * (1 - Math.max(progress, 0))
  return `translate3d(0, ${hiddenY}px, 0)`
}

function DesktopDimensionCard({ title, description, image, icon, iconBgColor, path, imageScale }) {
  return (
    <Box
      component={RouterLink}
      to={path}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: DESKTOP_CARD_WIDTH,
        maxWidth: '100%',
        bgcolor: '#fffefa',
        borderRadius: '20px',
        boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
        boxSizing: 'border-box',
        textDecoration: 'none',
        color: '#111',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          p: '20px',
          pb: '16px',
        }}
      >
        <FigmaIconFrame
          src={icon}
          bgColor={iconBgColor}
          boxSize={52}
          innerSize={32}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 20,
              lineHeight: 1.3,
              fontWeight: 400,
              color: '#111',
              mb: '4px',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: 1.5,
              fontWeight: 400,
              color: 'rgba(17,17,17,0.8)',
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          height: 140,
          flexShrink: 0,
          overflow: 'hidden',
          position: 'relative',
          bgcolor: '#f5f5f0',
        }}
      >
        <Box
          component="img"
          src={image}
          alt={title}
          sx={{
            position: 'absolute',
            inset: imageScale ? undefined : 0,
            width: imageScale?.width ?? '100%',
            height: imageScale?.height ?? '100%',
            left: imageScale?.left ?? 0,
            top: imageScale?.top ?? 0,
            maxWidth: 'none',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </Box>
    </Box>
  )
}

function DimensionCard({ title, description, icon, iconBgColor, path, side, progress, mobileLayout }) {
  const transform = revealTransformMobile(progress)

  return (
    <Box
      component={RouterLink}
      to={path}
      sx={{
        display: 'block',
        width: '100%',
        maxWidth: { xs: 362, sm: 440 },
        bgcolor: '#fffefa',
        borderRadius: '20px',
        boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.2)',
        p: '24px',
        boxSizing: 'border-box',
        textDecoration: 'none',
        color: '#111',
        opacity: progress,
        maxHeight: progress > 0 ? 'none' : 0,
        overflow: 'hidden',
        transform,
        transition: mobileLayout
          ? `opacity 580ms cubic-bezier(0.16, 1, 0.3, 1), transform 580ms cubic-bezier(0.16, 1, 0.3, 1)`
          : 'none',
        willChange: progress < 1 ? 'opacity, transform' : 'auto',
        pointerEvents: progress > 0.35 ? 'auto' : 'none',
        '@media (prefers-reduced-motion: reduce)': {
          opacity: 1,
          transform: 'none',
          transition: 'none',
          pointerEvents: 'auto',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 6, md: scale.gap.xl },
          alignItems: 'flex-start',
        }}
      >
        <FigmaIconFrame src={icon} bgColor={iconBgColor} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography
            sx={{
              fontSize: { xs: 22, md: 28 },
              lineHeight: 'normal',
              fontWeight: 400,
              color: '#111',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 16, md: 20 },
              lineHeight: 'normal',
              fontWeight: 400,
              color: '#111',
              opacity: 0.6,
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

function SectionHeader({ compact }) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        color: '#111',
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? { xs: 2, md: '20px' } : { xs: 2, md: '20px' },
        alignItems: 'center',
        px: { xs: 0, md: 2 },
      }}
    >
      <Typography
        component="h2"
        sx={{
          fontSize: sectionTitleFontSize,
          lineHeight: 'normal',
          fontWeight: 400,
          whiteSpace: 'pre-line',
          maxWidth: 730,
          [MOBILE_MQ]: { fontSize: 40, maxWidth: 323 },
        }}
      >
        {`The foundation works \nacross\u00a07 key dimensions\u00a0`}
      </Typography>
      <Typography
        sx={{
          fontSize: leadFontSize,
          lineHeight: 'normal',
          fontWeight: 400,
          color: '#111',
          opacity: 0.8,
          maxWidth: { xs: 362, md: 1200 },
          [MOBILE_MQ]: { fontSize: 18 },
        }}
      >
        {SECTION_SUBTITLE}
      </Typography>
    </Box>
  )
}

function MobileCardRow({ layout, index }) {
  const rowRef = useRef(null)
  const visible = useInViewReveal(rowRef)
  const dim = getDimension(layout.slug)
  if (!dim) return null

  const progress = visible ? 1 : 0
  const delay = index * 45

  return (
    <Box
      ref={rowRef}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        transitionDelay: `${delay}ms`,
      }}
    >
      <DimensionCard
        {...dim}
        side={layout.side}
        progress={progress}
        mobileLayout
      />
    </Box>
  )
}

function DesktopScrollSection() {
  const sectionRef = useRef(null)
  const cardRefs = useRef([])
  const reducedMotion = useReducedMotion()

  useDimensionsScroll(sectionRef, cardRefs, !reducedMotion)

  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{
        position: 'relative',
        width: '100%',
        bgcolor: '#fffefa',
      }}
    >
      <Box
        data-dimensions-pin
        sx={{
          minHeight: { lg: '100vh' },
          maxHeight: { lg: '100vh' },
          height: { lg: '100vh' },
          overflow: 'hidden',
          px: mobilePaddingX,
          boxSizing: 'border-box',
        }}
      >
        {/* Section copy — centered, sits behind cards as they cross the middle */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <Box sx={{ maxWidth: 1200, width: '100%', mx: 'auto', px: { lg: 2, xl: 0 } }}>
            <SectionHeader />
          </Box>
        </Box>

        {/* Cards — travel bottom → top on inset left / right lanes */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            maxWidth: 1520,
            mx: 'auto',
            width: '100%',
            pointerEvents: 'none',
          }}
        >
          {CARD_LAYOUT.map((layout, index) => {
            const dim = getDimension(layout.slug)
            if (!dim) return null

            const isLeft = layout.side === 'left'

            return (
              <Box
                key={layout.slug}
                ref={(node) => {
                  cardRefs.current[index] = node
                }}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: isLeft ? CARD_SIDE_INSET : 'auto',
                  right: isLeft ? 'auto' : CARD_SIDE_INSET,
                  width: DESKTOP_CARD_WIDTH,
                  maxWidth: { lg: '34%', xl: DESKTOP_CARD_WIDTH },
                  zIndex: 10 + index,
                  opacity: 0,
                  transform: 'translate3d(0, calc(-50% + 50vh), 0)',
                  visibility: 'hidden',
                  willChange: 'transform, opacity',
                  '@media (prefers-reduced-motion: reduce)': {
                    transform: 'translate3d(0, -50%, 0)',
                    opacity: 1,
                    visibility: 'visible',
                  },
                }}
              >
                <DesktopDimensionCard {...dim} />
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

function MobileStackSection() {
  return (
    <Box
      component="section"
      sx={{
        width: '100%',
        bgcolor: '#fffefa',
        py: { xs: '80px', md: 10 },
        px: mobilePaddingX,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          maxWidth: 1520,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 5, md: 8 },
        }}
      >
        <SectionHeader compact />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '12px', md: 2 },
            alignItems: 'center',
          }}
        >
          {CARD_LAYOUT.map((layout, index) => (
            <MobileCardRow key={layout.slug} layout={layout} index={index} />
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default function AboutDimensionsSection() {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const reducedMotion = useReducedMotion()

  if (isDesktop && !reducedMotion) {
    return <DesktopScrollSection />
  }

  return <MobileStackSection />
}
